import { Typewriter } from "../Typewriter";
import { Button } from "../ui/button";
import { ArrowRight } from "lucide-react";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";

// Small logo for the screenshot title bar
const TitleBarLogo = () => (
  <svg 
    viewBox="0 0 80 76" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    className="h-[18px] w-auto"
    aria-label="Veranda AI Logo"
  >
    <rect x="0" y="0" width="24" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="56" y="0" width="24" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="8" y="16" width="24" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="48" y="16" width="24" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="16" y="32" width="20" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="44" y="32" width="20" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="22" y="48" width="16" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="42" y="48" width="16" height="10" rx="1.5" fill="#ffffff"/>
    <rect x="30" y="64" width="20" height="10" rx="1.5" fill="#3b82f6"/>
  </svg>
);

export const HeroSection = ({ onRequestDemo }) => {
  const [headlineRef, headlineVisible] = useScrollAnimation({ threshold: 0.2 });
  const [screenshotRef, screenshotVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <section
      className="pt-[200px] pb-24 px-[5%] relative"
      style={{
        background:
          "radial-gradient(ellipse 80% 60% at 50% 100%, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0.04) 40%, transparent 70%)",
      }}
      data-testid="hero-section"
    >
      <div className="max-w-content mx-auto">
        <div 
          ref={headlineRef}
          className="max-w-4xl"
          style={{
            opacity: headlineVisible ? 1 : 0,
            transform: headlineVisible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.8s ease-out, transform 0.8s ease-out",
          }}
        >
          <h1
            className="text-[clamp(2.5rem,5.5vw,4.5rem)] font-medium tracking-[-1.5px] leading-[1.1] text-white mb-6"
            data-testid="hero-headline"
          >
            See, predict, and target every home that needs new <Typewriter />
          </h1>
          <p 
            className="text-[1.15rem] text-[#9ca3af] leading-[1.7] mb-8 max-w-xl"
            style={{
              opacity: headlineVisible ? 1 : 0,
              transform: headlineVisible ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.8s ease-out 0.15s, transform 0.8s ease-out 0.15s",
            }}
          >
            AI-powered demand intelligence for building products.
          </p>
          <div
            style={{
              opacity: headlineVisible ? 1 : 0,
              transform: headlineVisible ? "translateY(0)" : "translateY(20px)",
              transition: "opacity 0.8s ease-out 0.3s, transform 0.8s ease-out 0.3s",
            }}
          >
            <Button
              onClick={onRequestDemo}
              className="bg-[#3b82f6] hover:bg-[#2563eb] text-white font-semibold text-[0.95rem] rounded-[8px] px-6 py-3 transition-all duration-200 hover:-translate-y-[1px] shadow-glow hover:shadow-glow-hover animate-pulse-glow"
              data-testid="hero-cta-btn"
            >
              Request a Demo <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Product Screenshot */}
        <div 
          ref={screenshotRef}
          className="mt-16 relative"
          style={{
            opacity: screenshotVisible ? 1 : 0,
            transform: screenshotVisible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.98)",
            transition: "opacity 1s ease-out 0.2s, transform 1s ease-out 0.2s",
          }}
        >
          <div
            className="relative rounded-[12px] overflow-hidden border border-[#1e1e1e] bg-[#141414] shadow-2xl"
            style={{
              maxHeight: "75vh",
              WebkitMaskImage:
                "linear-gradient(to bottom, black 70%, transparent 95%)",
              maskImage:
                "linear-gradient(to bottom, black 70%, transparent 95%)",
            }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_home-scoring-kit/artifacts/hicb813l_Screenshot%202026-03-26%20at%209.59.52%E2%80%AFAM.png"
              alt="Veranda AI Platform - Property scoring and demand intelligence"
              className="w-full h-auto"
              data-testid="hero-screenshot"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
